import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import ProfileCard from "@/components/ProfileCard";
import { SlidersHorizontal, X } from "lucide-react";
import { toast } from "sonner";

export default function Directory() {
  const { t } = useLang();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [profiles, setProfiles] = useState([]);
  const [services, setServices] = useState([]);
  const [cities, setCities] = useState([]);
  const [meta, setMeta] = useState({ countries: [], categories: [], availability_types: [] });
  const [favorites, setFavorites] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [mobileFilters, setMobileFilters] = useState(false);

  const filters = useMemo(() => ({
    country_code: params.get("country") || "",
    city: params.get("city") || "",
    verified: params.get("verified") === "1",
    independent: params.get("independent") || "",
    service: params.get("service") || "",
    availability: params.get("availability") || "",
    available_now: params.get("available_now") === "1",
    min_price: params.get("min") || "",
    max_price: params.get("max") || "",
    min_age: params.get("min_age") || "",
    max_age: params.get("max_age") || "",
    language: params.get("language") || "",
    sort: params.get("sort") || "featured",
  }), [params]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = {};
      if (filters.country_code) q.country_code = filters.country_code;
      if (filters.city) q.city = filters.city;
      if (filters.verified) q.verified = true;
      if (filters.independent === "independent") q.independent = true;
      if (filters.independent === "agency") q.independent = false;
      if (filters.service) q.service = filters.service;
      if (filters.availability) q.availability = filters.availability;
      if (filters.available_now) q.available_now = true;
      if (filters.min_price) q.min_price = filters.min_price;
      if (filters.max_price) q.max_price = filters.max_price;
      if (filters.min_age) q.min_age = filters.min_age;
      if (filters.max_age) q.max_age = filters.max_age;
      if (filters.language) q.language = filters.language;
      if (filters.sort) q.sort = filters.sort;
      const { data } = await api.get("/profiles", { params: q });
      setProfiles(data.profiles);
    } catch (e) {
      console.warn("[Directory] load error:", e);
      toast.error("Could not load directory");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const [s, c] = await Promise.all([api.get("/meta/services"), api.get("/meta/countries")]);
        setServices(s.data.services || []);
        setMeta({
          categories: s.data.categories || [],
          availability_types: s.data.availability_types || [],
          countries: c.data.countries || [],
        });
      } catch (e) { console.warn("[Directory.jsx] non-blocking error:", e); }
      try {
        const { data } = await api.get("/meta/cities", {
          params: filters.country_code ? { country_code: filters.country_code } : {},
        });
        setCities(data.cities || []);
      } catch (e) { console.warn("[Directory.jsx] non-blocking error:", e); }
      if (user) {
        try {
          const { data } = await api.get("/favorites");
          setFavorites(new Set(data.favorites.map((f) => f.profile_id)));
        } catch (e) { console.warn("[Directory.jsx] non-blocking error:", e); }
      }
    })();
  }, [user, filters.country_code]);

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (!v) next.delete(k);
    else next.set(k, v);
    // When country changes, clear the city
    if (k === "country") next.delete("city");
    setParams(next, { replace: true });
  };

  const reset = () => setParams(new URLSearchParams(), { replace: true });

  const toggleFav = async (p) => {
    if (!user) { toast.error("Sign in to save favorites"); return; }
    const isFav = favorites.has(p.id);
    try {
      if (isFav) {
        await api.delete(`/favorites/${p.id}`);
        setFavorites((s) => { const n = new Set(s); n.delete(p.id); return n; });
      } else {
        await api.post(`/favorites/${p.id}`);
        setFavorites((s) => new Set(s).add(p.id));
      }
    } catch { toast.error("Action failed"); }
  };

  const Filters = (
    <aside className="lg:w-72 lg:shrink-0 space-y-6" data-testid="filters-panel">
      <div>
        <p className="text-overline mb-3">Country</p>
        <select value={filters.country_code} onChange={(e) => setFilter("country", e.target.value)} className="w-full bg-[#121212] border border-white/10 text-white px-3 py-2.5 rounded-md text-sm focus:border-gold/50 focus:outline-none" data-testid="filter-country">
          <option value="">All countries</option>
          {meta.countries.map((c) => <option key={c.code} value={c.code}>{c.name_en}</option>)}
        </select>
      </div>
      <div>
        <p className="text-overline mb-3">{t.filters.city}</p>
        <select value={filters.city} onChange={(e) => setFilter("city", e.target.value)} className="w-full bg-[#121212] border border-white/10 text-white px-3 py-2.5 rounded-md text-sm focus:border-gold/50 focus:outline-none" data-testid="filter-city">
          <option value="">{t.filters.all_cities}</option>
          {cities.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
        </select>
      </div>
      <div>
        <p className="text-overline mb-3">Type</p>
        <div className="grid grid-cols-3 gap-2 text-xs">
          {[{ k: "", l: "All" }, { k: "independent", l: t.filters.independent }, { k: "agency", l: t.filters.agency }].map((o) => (
            <button key={o.k} onClick={() => setFilter("independent", o.k)} className={`py-2 rounded border ${filters.independent === o.k ? "border-gold text-gold" : "border-white/10 text-[#a3a3a3]"}`} data-testid={`filter-type-${o.k || "all"}`}>{o.l}</button>
          ))}
        </div>
      </div>
      <div>
        <label className="flex items-center gap-2 text-sm text-[#e5e5e5] cursor-pointer">
          <input type="checkbox" checked={filters.verified} onChange={(e) => setFilter("verified", e.target.checked ? "1" : "")} className="accent-[#D4AF37]" data-testid="filter-verified" />
          {t.filters.verified_only}
        </label>
        <label className="flex items-center gap-2 text-sm text-[#e5e5e5] cursor-pointer mt-2">
          <input type="checkbox" checked={filters.available_now} onChange={(e) => setFilter("available_now", e.target.checked ? "1" : "")} className="accent-[#D4AF37]" data-testid="filter-available-now" />
          Available now
        </label>
      </div>
      <div>
        <p className="text-overline mb-3">Availability</p>
        <select value={filters.availability} onChange={(e) => setFilter("availability", e.target.value)} className="w-full bg-[#121212] border border-white/10 text-white px-3 py-2.5 rounded-md text-sm focus:border-gold/50 focus:outline-none" data-testid="filter-availability">
          <option value="">Any</option>
          {meta.availability_types.map((a) => <option key={a.slug} value={a.slug}>{a.label}</option>)}
        </select>
      </div>
      <div>
        <p className="text-overline mb-3">{t.filters.service}</p>
        <select value={filters.service} onChange={(e) => setFilter("service", e.target.value)} className="w-full bg-[#121212] border border-white/10 text-white px-3 py-2.5 rounded-md text-sm focus:border-gold/50 focus:outline-none" data-testid="filter-service">
          <option value="">{t.filters.any_service}</option>
          {services.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div>
        <p className="text-overline mb-3">Age</p>
        <div className="grid grid-cols-2 gap-2">
          <input type="number" placeholder="Min" value={filters.min_age} onChange={(e) => setFilter("min_age", e.target.value)} className="bg-[#121212] border border-white/10 text-white px-3 py-2.5 rounded-md text-sm focus:border-gold/50 focus:outline-none" data-testid="filter-min-age" />
          <input type="number" placeholder="Max" value={filters.max_age} onChange={(e) => setFilter("max_age", e.target.value)} className="bg-[#121212] border border-white/10 text-white px-3 py-2.5 rounded-md text-sm focus:border-gold/50 focus:outline-none" data-testid="filter-max-age" />
        </div>
      </div>
      <div>
        <p className="text-overline mb-3">Price (€)</p>
        <div className="grid grid-cols-2 gap-2">
          <input type="number" placeholder={t.filters.min_price} value={filters.min_price} onChange={(e) => setFilter("min", e.target.value)} className="bg-[#121212] border border-white/10 text-white px-3 py-2.5 rounded-md text-sm focus:border-gold/50 focus:outline-none" data-testid="filter-min" />
          <input type="number" placeholder={t.filters.max_price} value={filters.max_price} onChange={(e) => setFilter("max", e.target.value)} className="bg-[#121212] border border-white/10 text-white px-3 py-2.5 rounded-md text-sm focus:border-gold/50 focus:outline-none" data-testid="filter-max" />
        </div>
      </div>
      <div>
        <p className="text-overline mb-3">Language</p>
        <input placeholder="e.g. English" value={filters.language} onChange={(e) => setFilter("language", e.target.value)} className="w-full bg-[#121212] border border-white/10 text-white px-3 py-2.5 rounded-md text-sm focus:border-gold/50 focus:outline-none" data-testid="filter-language" />
      </div>
      <div>
        <p className="text-overline mb-3">{t.filters.sort}</p>
        <select value={filters.sort} onChange={(e) => setFilter("sort", e.target.value)} className="w-full bg-[#121212] border border-white/10 text-white px-3 py-2.5 rounded-md text-sm focus:border-gold/50 focus:outline-none" data-testid="filter-sort">
          <option value="featured">{t.filters.featured}</option>
          <option value="rating">{t.filters.rating}</option>
          <option value="price_asc">{t.filters.price_asc}</option>
          <option value="price_desc">{t.filters.price_desc}</option>
        </select>
      </div>
      <button onClick={reset} className="btn-ghost-dark rounded-md px-4 py-2.5 text-sm w-full" data-testid="filter-reset">{t.filters.reset}</button>
    </aside>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="directory-page">
      <div className="mb-8">
        <p className="text-overline">{filters.city || "All cities"}</p>
        <h1 className="font-heading text-4xl sm:text-5xl text-white mt-2 font-light tracking-tight">Directory</h1>
        <p className="text-sm text-[#a3a3a3] mt-2">{profiles.length} profiles</p>
      </div>

      <div className="flex lg:hidden mb-4">
        <button onClick={() => setMobileFilters(true)} className="btn-ghost-dark rounded-md px-4 py-2.5 text-sm inline-flex items-center gap-2" data-testid="mobile-filters-btn">
          <SlidersHorizontal className="w-4 h-4" strokeWidth={1.5} /> {t.filters.title}
        </button>
      </div>

      <div className="flex flex-col lg:flex-row gap-10">
        <div className="hidden lg:block">{Filters}</div>

        {mobileFilters && (
          <div className="fixed inset-0 z-50 bg-black/80 lg:hidden" onClick={() => setMobileFilters(false)}>
            <div className="absolute right-0 top-0 bottom-0 w-80 max-w-[90vw] bg-[#0a0a0a] border-l border-white/10 p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-6">
                <p className="font-heading text-2xl text-white">{t.filters.title}</p>
                <button onClick={() => setMobileFilters(false)}><X className="w-5 h-5 text-white" /></button>
              </div>
              {Filters}
            </div>
          </div>
        )}

        <div className="flex-1">
          {loading ? (
            <p className="text-[#737373]">{t.common.loading}</p>
          ) : profiles.length === 0 ? (
            <div className="card-luxe rounded-lg p-12 text-center">
              <p className="font-heading text-2xl text-white">No profiles match these filters</p>
              <button onClick={reset} className="mt-4 text-sm text-gold">{t.filters.reset}</button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-2 xl:grid-cols-3 gap-2.5 sm:gap-5 stagger">
              {profiles.map((p) => (
                <ProfileCard key={p.id} profile={p} isFavorite={favorites.has(p.id)} onFavorite={toggleFav} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
